import React, { ReactNode } from "react";
import { Link, useNavigate } from "@remix-run/react";
import { IoMdArrowRoundBack, IoMdClose } from "react-icons/io";
import { IoSearch } from "react-icons/io5";
import { RiHome3Fill } from "react-icons/ri";
import { cn } from "~/lib/utils";

import { Button, Modal, useModal } from "~/components";
import GetCreditsModal from "~/components/modals/get-credits";
import PromotionButton from "./promotion-button";
import CreditsButton from "./credits-button";
import SearchModal from "./modals/search-modal";
import { FBProfile } from "~/.server/api";
import DailyRewardTrigger from "./daily-reward-trigger";
import DailyRewardModal from "./modals/daily-reward-modal";
import ComponentSvg from "./component-svg";
import NotificationBell from "./notification-bell";
import UnlimitedMessagesPill from "./unlimited-messages-pill";
import UnlimitedActiveModal from "./modals/unlimited-active";
import UnlimitedBonusTeaser from "./unlimited-bonus-teaser";
import UnlimitedReengagementTeaser from "./unlimited-reengagement-teaser";

interface MobileHeaderProps {
  className?: string;
  children?: ReactNode;
  showBack?: boolean;
  showHome?: boolean;
  onBackClick?: () => void;
  prefix?: ReactNode;
  user?: FBProfile; // Made optional
  showPromotion?: boolean;
  showSearch?: boolean;
  onClaimReward?: (claimIndex: number) => void; // Made optional
  notificationsUnreadCount?: number;
  onNotificationsUnreadCountChange?: (count: number) => void;
  isChatScreen?: boolean;
  unlimitedNormalEligible?: boolean;
  reengagementOfferAvailable?: boolean;
  onReengagementClick?: () => void;
}

const MobileHeader: React.FC<MobileHeaderProps> = ({
  className,
  children,
  showBack,
  showHome,
  onBackClick,
  prefix,
  user, // Now optional
  showPromotion,
  showSearch,
  onClaimReward, // Destructure prop
  notificationsUnreadCount = 0,
  onNotificationsUnreadCountChange,
  isChatScreen = false,
  unlimitedNormalEligible = false,
  reengagementOfferAvailable = false,
  onReengagementClick,
}) => {
  const isUnlimitedActive = Boolean(
    user?.unlimited_messages_until &&
      new Date(user.unlimited_messages_until).getTime() > Date.now()
  );
  const navigate = useNavigate();
  const creditsModal = useModal();
  const searchModal = useModal();
  const dailyRewardModal = useModal(); // Add modal state
  const unlimitedActiveModal = useModal();

  return (
    <header
      className={cn(
        "z-10 bg-headerBackground/90 backdrop-blur-md border-b border-white/5 text-base leading-none",
        // "text-center" removed so we can control left/center/right
        "flex items-center justify-between px-4 py-2  text-text md:rounded-t-lg",
        className
      )}
    >
      {/* LEFT SECTION */}
      <div className="flex items-center gap-2 md:hidden">
        {/* Show back arrow if desired and no prefix */}
        {showBack && !prefix && (
          <IoMdArrowRoundBack
            size={24}
            onClick={() => {
              if (onBackClick) {
                onBackClick();
              } else {
                navigate(-1);
              }
            }}
            className="cursor-pointer"
          />
        )}

        {/* If prefix exists, render it here (e.g. a small icon, text, etc.) */}
        {prefix && prefix}

        {/* Show search icon if needed */}
        {showSearch && (
          <IoSearch
            size={24}
            className="cursor-pointer"
            onClick={searchModal.open}
          />
        )}
      </div>

      {/* CENTER SECTION */}
      <div className="flex-1 flex justify-center items-center">
        {/* Conditional Rendering Logic */}
        {showPromotion ? (
          // Show logo when showPromotion is true
          <ComponentSvg className="text-text h-6" />
        ) : (
          // Default Case: Show title/children
          <div className="font-bold">{children}</div>
        )}
      </div>

      {/* RIGHT SECTION */}
      <div className="flex items-center gap-3 pr-1">
        {/* Desktop close button (if you want to hide on mobile) */}
        <Button
          variant="ghost"
          className="hidden md:block p-2 hover:bg-primary w-15 h-10 rounded-full bg-background text-text border-0"
          onClick={() => {
            if (onBackClick) {
              onBackClick();
            } else {
              navigate(-1);
            }
          }}
          aria-label="Close"
        >
          <IoMdClose size={24} />
        </Button>

        {/* Curiosity teaser — chat screen only, eligible users, no active window. */}
        {isChatScreen && unlimitedNormalEligible && !isUnlimitedActive && user && (
          <UnlimitedBonusTeaser />
        )}
        {/* Persistent win-back entry — free hour, any screen, no active window. */}
        {reengagementOfferAvailable && !isUnlimitedActive && user && (
          <UnlimitedReengagementTeaser onClick={onReengagementClick} />
        )}

        {user && isUnlimitedActive && (
          <div className="flex items-center justify-center">
            <UnlimitedMessagesPill
              unlimitedMessagesUntil={user.unlimited_messages_until}
              onClick={unlimitedActiveModal.open}
              compact
            />
          </div>
        )}

        {user && (
          <div className="w-9 h-9 flex items-center justify-center">
            <NotificationBell
              unreadCount={notificationsUnreadCount}
              onUnreadCountChange={onNotificationsUnreadCountChange}
              iconSize={24}
            />
          </div>
        )}

        {/* Show home or credits on mobile if desired. Credits hidden during
            an active unlimited window — per-message charging is paused so
            the count is misleading. */}
        {showHome &&
          user && ( // Added null-check for user
            <div className="md:hidden flex items-center gap-3">
              {/* Daily Reward Trigger */}
              {user.metadata?.dailyReward && (
                <div className="w-9 h-9 flex items-center justify-center">
                  <DailyRewardTrigger
                    dailyRewardData={user.metadata.dailyReward}
                    onClick={dailyRewardModal.open}
                  />
                </div>
              )}

              {/* Credits button */}
              {!isUnlimitedActive && (
                <div className="w-9 h-9 flex items-center justify-center">
                  <CreditsButton
                    credits={user.credits}
                    onClick={creditsModal.open}
                  />
                </div>
              )}
            </div>
          )}
      </div>

      {/* Modals */}
      <Modal {...creditsModal.modalProps}>
        <GetCreditsModal countryCode={user?.country_code || "US"} source="header_coins" />{" "}
        {/* Added optional chaining */}
      </Modal>
      <Modal {...unlimitedActiveModal.modalProps}>
        <UnlimitedActiveModal
          endsAt={user?.unlimited_messages_until}
          onClose={unlimitedActiveModal.close}
        />
      </Modal>
      <Modal {...searchModal.modalProps}>
        <SearchModal onClose={searchModal.close} />
      </Modal>
      {/* Daily Rewards Modal */}
      <Modal {...dailyRewardModal.modalProps}>
        {user &&
          user.metadata?.dailyReward &&
          onClaimReward && ( // Added null-check for onClaimReward
            <DailyRewardModal
              dailyRewardData={user.metadata.dailyReward}
              onClose={dailyRewardModal.close}
              onClaim={onClaimReward}
              userCredits={user.credits}
              purchasesCount={user.purchases_count}
            />
          )}
      </Modal>
    </header>
  );
};

export default MobileHeader;
